import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "kf-proptypes/widgets";
import { Trans } from "@lingui/macro";

import {
  validTimeString,
  timeStringToMinutes,
  minutesToTimeString
} from "base/utils";

import { Tooltip } from "@widgets/feedback/index.jsx";

import styles from "./timepanel.css";
import commonstyles from "./datetime.css";

function TimePanelInput(props) {
  const { value, onChange, updateErrorState } = props;
  const [valid, setValid] = useState(validTimeString(value));
  const [currentValue, setCurrentValue] = useState(value);

  useEffect(
    function initEffect() {
      setCurrentValue(value);
      setValid(validTimeString(value));
      updateErrorState(!validTimeString(value));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value]
  );

  return (
    <input
      value={currentValue}
      className={`${commonstyles.inputField} ${
        !valid ? commonstyles.errorBorder : ""
      }`}
      onInput={(e) => {
        if (validTimeString(e.target.value)) {
          setValid(true);
          onChange(e.target.value);
          updateErrorState(false);
        } else {
          setValid(false);
          updateErrorState(true);
        }
        setCurrentValue(e.target.value);
      }}
    />
  );
}

TimePanelInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  updateErrorState: PropTypes.func
};

export function TimePanel(props) {
  const {
    interval = 15,
    value = 15,
    defaultValue = 15,
    onChange,
    updateErrorState,
    disabledFrom,
    disabledTill,
    errorMsg
  } = props;

  const [selectedValue, setSelectedValue] = useState(
    defaultValue > 720 ? defaultValue - 720 : defaultValue
  );
  const [selectedMeridian, setSelectedMeridian] = useState(
    defaultValue >= 720 ? "pm" : "am"
  );

  const [timeString, setTimeString] = useState(minutesToTimeString(value));

  const [inputValue, setInputValue] = useState(minutesToTimeString(value));
  const [showDisabledMsg, setShowDisabledMsg] = useState();
  const scrollRef = useRef(null);

  const TIME_LIST = useMemo(
    function intervalMemoized() {
      let times = [];
      for (let i = 0; i <= 11; i++) {
        for (let j = 0; j < Math.round(60 / interval); j++) {
          const calcValue = i * 60 + j * interval;
          let isDisabled = false;

          let minutes = calcValue;
          if (selectedMeridian === "pm") {
            minutes += 720;
          }

          if (disabledFrom) isDisabled = minutes > disabledFrom;
          if (disabledTill) isDisabled = minutes < disabledTill;

          times.push({
            value: calcValue,
            disabled: isDisabled,
            label: `${(i === 0 ? 12 : i).toString().padStart(2, "0")}:${(
              j * interval
            )
              .toString()
              .padStart(2, "0")}`
          });
        }
      }
      return times;
    },
    [interval, disabledTill, disabledFrom, selectedMeridian]
  );

  useEffect(
    function selectedEffect() {
      let timerId = null;
      if (scrollRef && scrollRef.current) {
        timerId = setTimeout(() => {
          const child = scrollRef.current.querySelector(`[data-active="true"]`);
          const parent = scrollRef.current;
          var parentRect = scrollRef.current.getBoundingClientRect();
          var parentViewableArea = {
            height: parent.clientHeight,
            width: parent.clientWidth
          };
          var childRect = child.getBoundingClientRect();
          var isViewable =
            childRect.top >= parentRect.top &&
            childRect.bottom <= parentRect.top + parentViewableArea.height;
          if (!isViewable) {
            const scrollTop =
              childRect.top - parentRect.top - parent.clientHeight / 2;
            const scrollBot =
              childRect.bottom - parentRect.bottom + parent.clientHeight / 2;
            if (Math.abs(scrollTop) < Math.abs(scrollBot)) {
              parent.scrollTop += scrollTop;
            } else {
              parent.scrollTop += scrollBot;
            }
          }
        }, 100);
      }
      return () => {
        if (timerId) {
          clearTimeout(timerId);
        }
      };
    },
    [selectedValue]
  );

  useEffect(
    function changeEffect() {
      if (value || value === 0) {
        setSelectedValue(value >= 720 ? value - 720 : value);
        setSelectedMeridian(value >= 720 ? "pm" : "am");
        setTimeString(minutesToTimeString(value));
      } else {
        setTimeString(inputValue);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value]
  );

  const onTimeChanged = function onTimeChanged(e) {
    const dataset = e.target.dataset;

    if (dataset.type === "time") {
      const finalValue =
        parseInt(dataset.value, 10) + (selectedMeridian === "pm" ? 720 : 0);
      setSelectedValue(parseInt(dataset.value, 10));
      setTimeString(minutesToTimeString(finalValue));
      updateErrorState(false);
      onChange && onChange(finalValue);
    }
    setShowDisabledMsg(false);
  };

  const onMeridianChanged = (e) => {
    const dataset = e.target.dataset;

    const finalValue = selectedValue + (dataset.value === "pm" ? 720 : 0);

    if (
      (disabledFrom && disabledFrom < finalValue) ||
      (disabledTill && disabledTill > finalValue)
    ) {
      setShowDisabledMsg(true);
    } else {
      setShowDisabledMsg(false);
      onChange && onChange(finalValue);
    }
    setSelectedMeridian(dataset.value);
    setTimeString(minutesToTimeString(finalValue));
  };

  return (
    <div className={styles.timepanel}>
      <div className={styles.timeinputpanel}>
        <div className={styles.inputContainer}>
          <TimePanelInput
            value={timeString}
            onChange={(timeString) => {
              let value = timeStringToMinutes(timeString);
              setInputValue(timeString);
              let calcValue = value >= 720 ? value - 720 : value;
              if (
                (disabledFrom && disabledFrom < value) ||
                (disabledTill && disabledTill > value)
              ) {
                setShowDisabledMsg(true);
              } else {
                setShowDisabledMsg(false);
                onChange && onChange(value, timeString);
                setSelectedValue(calcValue);
                setSelectedMeridian(value >= 720 ? "pm" : "am");
              }
            }}
            updateErrorState={updateErrorState}
          />
          {showDisabledMsg && (
            <Tooltip
              content={
                errorMsg || (
                  <Trans>Start date must be before the end date</Trans>
                )
              }
              alwaysVisible={true}
              // position={TOOLTIP_POSITION.RIGHT}
            >
              <div />
            </Tooltip>
          )}
        </div>
      </div>
      <div className={styles.timelistpanel}>
        <div className={styles.timelistpanelinner}>
          <div className={styles.timelistscroll} ref={scrollRef}>
            <div className={styles.timelist} onClick={(e) => onTimeChanged(e)}>
              {TIME_LIST.map((time) => {
                const exactActive = time.value === selectedValue;
                const isNear =
                  selectedValue > time.value &&
                  selectedValue < time.value + interval;
                return (
                  <React.Fragment key={time.value}>
                    <button
                      data-value={time.value}
                      data-type="time"
                      {...(exactActive ? { "data-active": "true" } : {})}
                      className={`${styles.time} ${
                        exactActive ? styles.timeactive : ""
                      } ${time.disabled ? styles.disabled : ""}`}
                    >
                      {time.label}
                    </button>
                    {isNear && (
                      <div
                        data-active="true"
                        className={styles.intermediatetime}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
        <div className={styles.meridians}>
          <button
            onClick={onMeridianChanged}
            data-type="meridian"
            data-value="am"
            className={`${styles.time} ${styles.meridian} ${
              selectedMeridian === "am" ? styles.timeactive : ""
            }`}
          >
            AM
          </button>
          <button
            onClick={onMeridianChanged}
            className={`${styles.time} ${styles.meridian} ${
              selectedMeridian === "pm" ? styles.timeactive : ""
            }`}
            data-type="meridian"
            data-value="pm"
          >
            PM
          </button>
        </div>
      </div>
    </div>
  );
}

TimePanel.propTypes = {
  /**
   * interval for timepannel
   */
  interval: PropTypes.number,
  /**
   * value which will changed by user for timepannel
   */
  value: PropTypes.number,
  /**
   * default value for timepannel
   */
  defaultValue: PropTypes.number,
  /**
   * timepannel onchange for changing time intervals
   */
  onChange: PropTypes.func,
  /**
   * Its a callback function trigger when error occurs
   */
  updateErrorState: PropTypes.func,
  /**
   * This prop is used to disabled from date
   */
  disabledFrom: PropTypes.number,
  /**
   * This prop is used to disabled till date
   */
  disabledTill: PropTypes.number,
  /**
   * Error msg ti display if any error
   */
  errorMsg: PropTypes.string
};

TimePanel.defaultProps = {
  interval: 15,
  value: 15,
  defaultValue: 15
};
